import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    CommonEquityTerm,
    ConsiderationField,
    ConsiderationFieldChange,
    ConsiderationRowState,
    ConsiderationSection,
    ConsiderationTermBase,
    DeferredFixedPaymentTerm,
    DeferredVariablePaymentTerm,
    JvEquityTerm,
    PreferredEquityTerm,
    PutCallRight,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { TypeOfConsideration } from '../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { EM_DASH, considerationTypeName } from '../../../utils/flowOfFundsFormat';
import { toWireValue } from '../../../utils/flow-of-funds/consideration-field-format';
import { EditableConsiderationCell } from './EditableConsiderationCell';
import { ConsiderationCommentCell } from './ConsiderationCommentCell';
import { ConsiderationRemovalAction } from './ConsiderationRemovalAction';
import { ReadOnlyApproval } from '../ApprovalControl/ApprovalControl';
import { AddConsiderationRow } from '../../../slices/marketplace/flow-of-funds/thunks/add-consideration-row.thunk';
import { useConsiderationAutoSaveReporter } from './ConsiderationAutoSaveContext';

export const Fixed = ({ value }: { value: string }) => (
    <td>
        <span className="ff-detail__fixed">{value || EM_DASH}</span>
    </td>
);

const REMOVAL_REQUESTED_LABEL = 'Removal requested';

const RowStateBadge = ({ rowState }: { rowState: ConsiderationRowState }) =>
    rowState === ConsiderationRowState.RemovalRequested ? (
        <span className="ff-detail__row-state ff-detail__row-state--removal">
            {REMOVAL_REQUESTED_LABEL}
        </span>
    ) : null;

const isRowMuted = (rowState: ConsiderationRowState): boolean =>
    rowState === ConsiderationRowState.RemovalRequested;

interface EditableCellContext {
    section: ConsiderationSection;
    rowKey: number;
    editableFields: ConsiderationField[];
    fieldChanges: ConsiderationFieldChange[];
    engagementId: number;
}

const contextFor = (
    term: ConsiderationTermBase,
    section: ConsiderationSection,
    engagementId: number
): EditableCellContext => ({
    section,
    rowKey: term.id,
    editableFields: term.editableFields,
    fieldChanges: term.fieldChanges,
    engagementId,
});

const cellFor = (
    field: ConsiderationField,
    typedValue: string | number | boolean | null,
    context: EditableCellContext
) => (
    <EditableConsiderationCell
        key={field}
        field={field}
        wireValue={toWireValue(field, typedValue) ?? null}
        section={context.section}
        rowKey={context.rowKey}
        editableFields={context.editableFields}
        fieldChanges={context.fieldChanges}
        engagementId={context.engagementId}
        variant="chip"
    />
);

type CommentKind = 'admin' | 'buyer' | 'seller';

const isOwnCommentColumn = (kind: CommentKind, viewerParty: TimelineParty | null): boolean => {
    if (kind === 'admin') {
        return viewerParty === TimelineParty.VetValue;
    }
    if (kind === 'buyer') {
        return viewerParty === TimelineParty.Buyer;
    }
    return viewerParty === TimelineParty.Seller || viewerParty === TimelineParty.Broker;
};

interface TermTailProps {
    idPrefix: string;
    term: ConsiderationTermBase;
    context: EditableCellContext;
    viewerParty: TimelineParty | null;
}

const TermTail = ({ idPrefix, term, context, viewerParty }: TermTailProps) => (
    <>
        <ConsiderationCommentCell
            value={term.adminComment}
            uniqueId={`${idPrefix}-admin-comment-${term.id}`}
            isEditable={isOwnCommentColumn('admin', viewerParty) && term.canEditComment}
            section={context.section}
            rowKey={context.rowKey}
        />
        <ConsiderationCommentCell
            value={term.buyerComment}
            uniqueId={`${idPrefix}-buyer-comment-${term.id}`}
            isEditable={isOwnCommentColumn('buyer', viewerParty) && term.canEditComment}
            section={context.section}
            rowKey={context.rowKey}
        />
        <ConsiderationCommentCell
            value={term.sellerComment}
            uniqueId={`${idPrefix}-seller-comment-${term.id}`}
            isEditable={isOwnCommentColumn('seller', viewerParty) && term.canEditComment}
            section={context.section}
            rowKey={context.rowKey}
        />
        {cellFor(
            ConsiderationField.BuyerCalculationPeriodDays,
            term.buyerCalculationPeriodDays.currentValue,
            context
        )}
        {cellFor(
            ConsiderationField.SellerResponsePeriodDays,
            term.sellerResponsePeriodDays.currentValue,
            context
        )}
        {cellFor(
            ConsiderationField.PaymentDateOffsetDays,
            term.paymentDateOffsetDays.currentValue,
            context
        )}
        {cellFor(ConsiderationField.PutCallRight, term.putCallRight, context)}
        {term.putCallRight === PutCallRight.Na ? (
            <td />
        ) : (
            cellFor(ConsiderationField.PutCallExplanation, term.putCallExplanation, context)
        )}
        <td className="ff-approval__cell">
            <ReadOnlyApproval approval={term.buyerApproval} />
        </td>
        <td className="ff-approval__cell">
            <ReadOnlyApproval approval={term.sellerApproval} />
        </td>
    </>
);

export interface DeferredFixedPaymentRowProps {
    term: DeferredFixedPaymentTerm;
    paymentNumber: number;
    engagementId: number;
    viewerParty: TimelineParty | null;
    showActions: boolean;
}

export const DeferredFixedPaymentRow = ({
    term,
    paymentNumber,
    engagementId,
    viewerParty,
    showActions,
}: DeferredFixedPaymentRowProps) => {
    const context = contextFor(term, ConsiderationSection.DeferredFixedPayments, engagementId);
    const paymentLabel = `Payment ${paymentNumber}`;

    return (
        <tr className={isRowMuted(term.rowState) ? 'ff-detail__row--muted' : undefined}>
            <td className="ff-detail__row-label">
                {paymentLabel}
                <RowStateBadge rowState={term.rowState} />
            </td>
            {cellFor(ConsiderationField.PaymentTiming, term.paymentTiming, context)}
            {cellFor(ConsiderationField.Amount, term.amount, context)}
            {cellFor(
                ConsiderationField.AnnualInterestAccrualRate,
                term.annualInterestAccrualRate,
                context
            )}
            {cellFor(ConsiderationField.IsContingent, term.isContingent, context)}
            {cellFor(
                ConsiderationField.ContingencyDependentVariable,
                term.contingencyDependentVariable,
                context
            )}
            {cellFor(
                ConsiderationField.ContingencyMinimumValue,
                term.contingencyMinimumValue,
                context
            )}
            {cellFor(
                ConsiderationField.HasEmploymentContingency,
                term.hasEmploymentContingency,
                context
            )}
            {cellFor(
                ConsiderationField.EmploymentTailPeriodMonths,
                term.employmentTailPeriodMonths,
                context
            )}
            <TermTail idPrefix="dfp" term={term} context={context} viewerParty={viewerParty} />
            {showActions && (
                <td>
                    <ConsiderationRemovalAction
                        canRequestRemoval={term.canRequestRemoval}
                        label={paymentLabel}
                        section={context.section}
                        rowKey={context.rowKey}
                        engagementId={engagementId}
                    />
                </td>
            )}
        </tr>
    );
};

export interface DeferredVariablePaymentRowProps {
    term: DeferredVariablePaymentTerm;
    paymentNumber: number;
    idPrefix: string;
    engagementId: number;
    viewerParty: TimelineParty | null;
    showActions: boolean;
}

export const DeferredVariablePaymentRow = ({
    term,
    paymentNumber,
    idPrefix,
    engagementId,
    viewerParty,
    showActions,
}: DeferredVariablePaymentRowProps) => {
    const context = contextFor(term, ConsiderationSection.DeferredVariablePayments, engagementId);
    const paymentLabel = `Payment ${paymentNumber}`;

    return (
        <tr className={isRowMuted(term.rowState) ? 'ff-detail__row--muted' : undefined}>
            <td className="ff-detail__row-label">
                {paymentLabel}
                <RowStateBadge rowState={term.rowState} />
            </td>
            {cellFor(ConsiderationField.PaymentTiming, term.paymentTiming, context)}
            <Fixed value={considerationTypeName(term.considerationType)} />
            {cellFor(ConsiderationField.ValueAtClosing, term.valueAtClosing, context)}
            {cellFor(
                ConsiderationField.CalculationDependentVariable,
                term.calculationDependentVariable,
                context
            )}
            {cellFor(ConsiderationField.CalcDvBaselineValue, term.calcDvBaselineValue, context)}
            {cellFor(ConsiderationField.CalculationMultiple, term.calculationMultiple, context)}
            {cellFor(ConsiderationField.MaximumPayment, term.maximumPayment, context)}
            {cellFor(
                ConsiderationField.HasEmploymentContingency,
                term.hasEmploymentContingency,
                context
            )}
            {cellFor(
                ConsiderationField.EmploymentTailPeriodMonths,
                term.employmentTailPeriodMonths,
                context
            )}
            {cellFor(ConsiderationField.PenaltyMultiple, term.penaltyMultiple, context)}
            {cellFor(
                ConsiderationField.DistributionPeriodicity,
                term.distributionPeriodicity,
                context
            )}
            <TermTail idPrefix={idPrefix} term={term} context={context} viewerParty={viewerParty} />
            {showActions && (
                <td>
                    <ConsiderationRemovalAction
                        canRequestRemoval={term.canRequestRemoval}
                        label={paymentLabel}
                        section={context.section}
                        rowKey={context.rowKey}
                        engagementId={engagementId}
                    />
                </td>
            )}
        </tr>
    );
};

export interface JvEquityRowProps {
    term: JvEquityTerm;
    engagementId: number;
    viewerParty: TimelineParty | null;
}

export const JvEquityRow = ({ term, engagementId, viewerParty }: JvEquityRowProps) => {
    const context = contextFor(term, ConsiderationSection.JvEquity, engagementId);

    return (
        <tr>
            {cellFor(ConsiderationField.ValueAtClosing, term.valueAtClosing, context)}
            {cellFor(ConsiderationField.PaymentTiming, term.paymentTiming, context)}
            {cellFor(
                ConsiderationField.CalculationDependentVariable,
                term.calculationDependentVariable,
                context
            )}
            {cellFor(ConsiderationField.CalcDvBaselineValue, term.calcDvBaselineValue, context)}
            {cellFor(ConsiderationField.CalculationMultiple, term.calculationMultiple, context)}
            {cellFor(
                ConsiderationField.HasEmploymentContingency,
                term.hasEmploymentContingency,
                context
            )}
            {cellFor(
                ConsiderationField.EmploymentTailPeriodMonths,
                term.employmentTailPeriodMonths,
                context
            )}
            {cellFor(ConsiderationField.PenaltyMultiple, term.penaltyMultiple, context)}
            {cellFor(
                ConsiderationField.DistributionPeriodicity,
                term.distributionPeriodicity,
                context
            )}
            <TermTail idPrefix="jv" term={term} context={context} viewerParty={viewerParty} />
        </tr>
    );
};

export interface CommonEquityRowProps {
    term: CommonEquityTerm;
    engagementId: number;
    viewerParty: TimelineParty | null;
}

export const CommonEquityRow = ({ term, engagementId, viewerParty }: CommonEquityRowProps) => {
    const context = contextFor(term, ConsiderationSection.CommonEquity, engagementId);

    return (
        <tr>
            {cellFor(ConsiderationField.ValueAtClosing, term.valueAtClosing, context)}
            {cellFor(ConsiderationField.ExpectedTimingToRecap, term.expectedTimingToRecap, context)}
            {cellFor(
                ConsiderationField.ExpectedPayoutMultiple,
                term.expectedPayoutMultiple,
                context
            )}
            <TermTail idPrefix="common" term={term} context={context} viewerParty={viewerParty} />
        </tr>
    );
};

export interface PreferredEquityRowProps {
    term: PreferredEquityTerm;
    engagementId: number;
    viewerParty: TimelineParty | null;
}

export const PreferredEquityRow = ({
    term,
    engagementId,
    viewerParty,
}: PreferredEquityRowProps) => {
    const context = contextFor(term, ConsiderationSection.PreferredEquity, engagementId);

    return (
        <tr>
            {cellFor(ConsiderationField.ValueAtClosing, term.valueAtClosing, context)}
            {cellFor(ConsiderationField.AnnualDividendRate, term.annualDividendRate, context)}
            {cellFor(ConsiderationField.DividendPaymentTiming, term.dividendPaymentTiming, context)}
            {cellFor(ConsiderationField.CompoundingFrequency, term.compoundingFrequency, context)}
            {cellFor(ConsiderationField.ExpectedTimingToRecap, term.expectedTimingToRecap, context)}
            {cellFor(
                ConsiderationField.IsConvertibleIntoCommon,
                term.isConvertibleIntoCommon,
                context
            )}
            {cellFor(ConsiderationField.ConversionRatio, term.conversionRatio, context)}
            <TermTail
                idPrefix="preferred"
                term={term}
                context={context}
                viewerParty={viewerParty}
            />
        </tr>
    );
};

export interface DeferredFixedPaymentAddRowProps {
    columnCount: number;
    engagementId: number;
}

export const DeferredFixedPaymentAddRow = ({
    columnCount,
    engagementId,
}: DeferredFixedPaymentAddRowProps) => {
    const dispatch = useDispatch() as any;
    const reporter = useConsiderationAutoSaveReporter();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addRow = async () => {
        setIsSubmitting(true);
        reporter.reportStart();
        const action = await dispatch(
            AddConsiderationRow({
                engagementId,
                section: ConsiderationSection.DeferredFixedPayments,
                values: [],
            })
        );
        reporter.reportOutcome(AddConsiderationRow.fulfilled.match(action) ? 'ok' : 'error');
        setIsSubmitting(false);
    };

    return (
        <tr>
            <td colSpan={columnCount} className="ff-detail__add-cell">
                <button
                    type="button"
                    className="ff-detail__add-trigger"
                    disabled={isSubmitting}
                    onClick={addRow}>
                    + Add payment
                </button>
            </td>
        </tr>
    );
};

export interface DeferredVariablePaymentAddRowProps {
    columnCount: number;
    engagementId: number;
    considerationType: TypeOfConsideration;
}

export const DeferredVariablePaymentAddRow = ({
    columnCount,
    engagementId,
    considerationType,
}: DeferredVariablePaymentAddRowProps) => {
    const dispatch = useDispatch() as any;
    const reporter = useConsiderationAutoSaveReporter();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addRow = async () => {
        setIsSubmitting(true);
        reporter.reportStart();
        const action = await dispatch(
            AddConsiderationRow({
                engagementId,
                section: ConsiderationSection.DeferredVariablePayments,
                values: [
                    {
                        field: ConsiderationField.DeferredVariableType,
                        value:
                            toWireValue(
                                ConsiderationField.DeferredVariableType,
                                String(considerationType)
                            ) ?? null,
                    },
                ],
            })
        );
        reporter.reportOutcome(AddConsiderationRow.fulfilled.match(action) ? 'ok' : 'error');
        setIsSubmitting(false);
    };

    return (
        <tr>
            <td colSpan={columnCount} className="ff-detail__add-cell">
                <button
                    type="button"
                    className="ff-detail__add-trigger"
                    disabled={isSubmitting}
                    onClick={addRow}>
                    + Add payment
                </button>
            </td>
        </tr>
    );
};
